import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShoppingBag, Boxes, Phone } from "lucide-react";
import { PageHero, useSeo } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { COMPANY, PROMO_PORTAL_URL } from "../lib/site";

const PortalCard = ({ icon: Icon, title, body, action }) => (
  <div className="group flex h-full flex-col border border-brand-line bg-white p-8 transition-colors hover:border-brand-red sm:p-10">
    <span className="flex h-12 w-12 items-center justify-center bg-brand-black text-white transition-colors group-hover:bg-brand-red">
      <Icon className="h-6 w-6" aria-hidden="true" />
    </span>
    <h2 className="mt-6 text-xl font-extrabold tracking-tight text-brand-black sm:text-2xl">{title}</h2>
    <p className="mt-3 flex-1 text-sm leading-relaxed text-brand-gray sm:text-base">{body}</p>
    <div className="mt-8">{action}</div>
  </div>
);

export default function ClientPortal() {
  useSeo(
    "Client Portal | MJG Promotions",
    "Access the MJG Promotions client portals — browse and order branded merchandise, or manage inventory and fulfillment programs."
  );
  const btn = "group/btn inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold transition-colors";
  return (
    <>
      <PageHero
        eyebrow="Client Access"
        title="Client Portal"
        intro="Choose the portal that matches what you need to do. If you're not sure which one applies to your program, call us and we'll point you in the right direction."
      />
      <section className="bg-brand-warm py-16 sm:py-24" data-testid="portal-hub">
        <div className="mx-auto max-w-5xl px-5">
          <div className="grid gap-6 md:grid-cols-2">
            <Reveal>
              <PortalCard
                icon={ShoppingBag}
                title="Promotional Products Catalog"
                body="Browse the MJG catalog of promotional products, apparel, and branded merchandise. Search thousands of items, view pricing, and start an order."
                action={
                  <a
                    href={PROMO_PORTAL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="portal-promo-button"
                    className={`${btn} bg-brand-red text-white hover:bg-brand-redhover`}
                  >
                    Open Promotional Portal
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </a>
                }
              />
            </Reveal>
            <Reveal delay={0.1}>
              <PortalCard
                icon={Boxes}
                title="Inventory & Fulfillment Portal"
                body="For clients on a managed fulfillment program: view inventory, place fulfillment requests, and track shipments through your dedicated portal."
                action={
                  <Link
                    to="/client-portal/fulfillment"
                    data-testid="portal-fulfillment-button"
                    className={`${btn} bg-brand-black text-white hover:bg-brand-charcoal`}
                  >
                    Go to Fulfillment Portal
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </Link>
                }
              />
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="mt-10 flex flex-wrap items-center gap-2 text-sm text-brand-gray">
              Need help accessing your account?
              <a href={COMPANY.phoneHref} data-testid="portal-help-phone" className="inline-flex items-center gap-1.5 font-semibold text-brand-red hover:underline">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
              or email
              <a href={COMPANY.emailHref} className="font-semibold text-brand-red hover:underline">{COMPANY.email}</a>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
